import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";
import AddCommentIcon from "@material-ui/icons/AddComment";
import GetAppIcon from "@material-ui/icons/GetApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import OsInteractionDialog from "../../components/OsInteractionDialog";
import OsRelationshipDialog from "../../components/OsRelationshipDialog";
import OsCreateForm from "../../components/OsCreateForm";
import useOsCatalogs from "../../hooks/useOsCatalogs";
import { osDateTimeInput } from "../../utils/osDates";

const useStyles = makeStyles(theme => ({
  pagePaper: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  formContent: {
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5)
  },
  summaryCard: {
    height: "100%",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  summaryItem: {
    marginBottom: theme.spacing(1.5)
  },
  tabs: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  tabContent: {
    padding: theme.spacing(2),
    minHeight: 250
  },
  descriptionField: {
    "& textarea": {
      fontFamily: "inherit",
      lineHeight: 1.5
    }
  },
  timelineItem: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderLeft: `3px solid ${theme.palette.primary.main}`
  },
  imagePreviewContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 320,
    backgroundColor: theme.palette.grey[100]
  },
  imagePreview: {
    display: "block",
    maxWidth: "100%",
    maxHeight: "75vh",
    objectFit: "contain"
  },
  preWrap: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  empty: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(4)
  },
  headerStatus: {
    marginLeft: theme.spacing(1)
  }
}));

const emptyForm = {
  grupo_id: "",
  cliente_id: "",
  contato_id: "",
  objeto_id: "",
  objeto_padrao_id: "",
  tecnico_id: "",
  status_id: "",
  prioridade_id: "",
  tipo_id: "",
  canal_id: "",
  descricao_curta: "",
  descricao: "",
  data_previsao_inicio: "",
  data_previsao_termino: "",
  cobrar: false,
  valor_total: 0,
  tempo_estimado: 0
};

const emptyCatalogs = {
  grupos: [],
  status: [],
  prioridades: [],
  tipos: [],
  canais: [],
  tecnicos: [],
  objetos: [],
  objetosConclusao: [],
  objetosPadrao: [],
  clientes: [],
  contatos: []
};

const items = response => response?.data?.data || [];

// Previsão é horário de parede (sem timezone): extrai direto da string.
// Usar `new Date` aqui deslocaria -3h (BRT) a cada abrir+salvar.
// Ver frontend/src/utils/osDates.js
const dateTimeInput = value => osDateTimeInput(value);

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
};

const formatMinutes = value => {
  if (value === null || value === undefined) return "-";
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "-";
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return hours ? `${hours}h ${remaining}min` : `${remaining}min`;
};

const periodDuration = period => {
  const start = new Date(period.data_hora_inicial);
  const end = period.data_hora_final
    ? new Date(period.data_hora_final)
    : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "-";
  return formatMinutes(Math.max(0, Math.floor((end - start) / 60000)));
};

const OrdensServicoDetalhe = () => {
  const classes = useStyles();
  const history = useHistory();
  const { ordemServicoId } = useParams();
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [catalogs, setCatalogs] = useState(emptyCatalogs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [clientSearch, setClientSearch] = useState("");
  const [interactionOpen, setInteractionOpen] = useState(false);
  const [relationshipOpen, setRelationshipOpen] = useState(false);
  const [createN2Open, setCreateN2Open] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRequest = useRef(0);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const {
    data: sharedCatalogs,
    error: sharedCatalogsError
  } = useOsCatalogs();

  const loadContacts = useCallback(async clienteId => {
    if (!clienteId) {
      setCatalogs(previous => ({ ...previous, contatos: [] }));
      return;
    }
    try {
      const response = await api.get(`/os/terceiros/${clienteId}/contatos`, {
        params: { per_page: 200 }
      });
      setCatalogs(previous => ({
        ...previous,
        contatos: items(response)
      }));
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/os/ordens-servico/${ordemServicoId}`
      );
      const loaded = data.data || data;
      setOrder(loaded);
      setForm({
        grupo_id: loaded.grupo_id || "",
        cliente_id: loaded.cliente_id || "",
        contato_id: loaded.contato_id || "",
        objeto_id: loaded.objeto_id || "",
        objeto_padrao_id: loaded.objeto_padrao_id || "",
        tecnico_id: loaded.tecnico_id || "",
        status_id: loaded.status_id || "",
        prioridade_id: loaded.prioridade_id || "",
        tipo_id: loaded.tipo_id || "",
        canal_id: loaded.canal_id || "",
        descricao_curta: loaded.descricao_curta || "",
        descricao: loaded.descricao || "",
        data_previsao_inicio: dateTimeInput(loaded.data_previsao_inicio),
        data_previsao_termino: dateTimeInput(loaded.data_previsao_termino),
        cobrar: Boolean(loaded.cobrar),
        valor_total: loaded.valor_total || 0,
        tempo_estimado: loaded.tempo_estimado || 0
      });
      if (loaded.cliente) {
        setCatalogs(previous => ({
          ...previous,
          clientes: [
            loaded.cliente,
            ...previous.clientes.filter(item => item.id !== loaded.cliente.id)
          ]
        }));
      }
      if (loaded.grupo_id) {
        try {
          const objectsResponse = await api.get("/os/objetos", {
            params: { grupo_id: loaded.grupo_id, per_page: 200 }
          });
          setCatalogs(previous => ({
            ...previous,
            objetos: items(objectsResponse)
          }));
        } catch (err) {
          toastError(err);
        }
      }
      await loadContacts(loaded.cliente_id);
    } catch (err) {
      toastError(err);
      history.push("/ordens-servico");
    } finally {
      setLoading(false);
    }
  }, [history, loadContacts, ordemServicoId]);

  const loadChats = useCallback(async () => {
    setChatsLoading(true);
    try {
      const { data } = await api.get(
        `/os/ordens-servico/${ordemServicoId}/chats`
      );
      setChats(data.data || []);
      setChatsLoaded(true);
    } catch (err) {
      toastError(err);
    } finally {
      setChatsLoading(false);
    }
  }, [ordemServicoId]);

  useEffect(() => {
    if (!sharedCatalogs) return;
    setCatalogs(previous => ({ ...previous, ...sharedCatalogs }));
  }, [sharedCatalogs]);

  useEffect(() => {
    if (sharedCatalogsError) toastError(sharedCatalogsError);
  }, [sharedCatalogsError]);

  useEffect(() => {
    let active = true;

    const loadClients = async () => {
      try {
        const response = await api.get("/os/clientes", {
          params: { per_page: 100 }
        });
        if (!active) return;
        setCatalogs(previous => ({
          ...previous,
          clientes: [...previous.clientes, ...items(response)].filter(
            (client, index, all) =>
              all.findIndex(item => item.id === client.id) === index
          )
        }));
      } catch (err) {
        if (active) toastError(err);
      }
    };

    loadClients();
    loadOrder();
    return () => {
      active = false;
    };
  }, [loadOrder]);

  useEffect(() => {
    if (!clientSearch.trim()) return undefined;
    let active = true;
    const timeout = setTimeout(async () => {
      try {
        const response = await api.get("/os/clientes", {
          params: { q: clientSearch, per_page: 50 }
        });
        if (active) {
          setCatalogs(previous => ({
            ...previous,
            clientes: [
              ...previous.clientes.filter(
                item => String(item.id) === String(form.cliente_id)
              ),
              ...items(response)
            ].filter(
              (client, index, all) =>
                all.findIndex(item => item.id === client.id) === index
            )
          }));
        }
      } catch (err) {
        if (active) toastError(err);
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [clientSearch, form.cliente_id]);

  useEffect(() => {
    if (tab === 7 && !chatsLoaded) loadChats();
  }, [chatsLoaded, loadChats, tab]);

  useEffect(() => {
    setChats([]);
    setChatsLoaded(false);
  }, [ordemServicoId]);

  const change = event => {
    const { name, value, checked, type } = event.target;
    setForm(previous => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "objeto_id" ? { objeto_padrao_id: "" } : {}),
      ...(name === "grupo_id" ? { status_id: "", tipo_id: "" } : {})
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        cobrar: Boolean(form.cobrar),
        valor_total: Number(form.valor_total || 0),
        tempo_estimado: Number(form.tempo_estimado || 0)
      };

      if (!form.contato_id) {
        delete payload.contato_id;
      }

      await api.put(`/os/ordens-servico/${ordemServicoId}`, payload);
      toast.success("Ordem de serviço atualizada.");
      await loadOrder();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const interactionSaved = async () => {
    setInteractionOpen(false);
    setTab(1);
    await loadOrder();
  };

  const relationshipSaved = async () => {
    setRelationshipOpen(false);
    setTab(3);
    await loadOrder();
  };

  const downloadAttachment = async attachment => {
    try {
      const response = await api.get(
        `/os/ordens-servico/${ordemServicoId}/anexos/${attachment.id}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const openPdf = async () => {
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = `OS #${ordemServicoId}`;
      previewWindow.document.body.innerHTML =
        '<p style="font-family: sans-serif; padding: 24px;">Preparando o PDF da ordem de serviço...</p>';
    }

    setPdfLoading(true);
    try {
      const response = await api.get(
        `/os/ordens-servico/${ordemServicoId}/pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.replace(url);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `OS-${ordemServicoId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    } catch (err) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      toastError(err);
    } finally {
      setPdfLoading(false);
    }
  };

  const isImageAttachment = attachment => {
    const type = String(attachment?.tipo || "").toLowerCase();
    const name = String(attachment?.nome_arquivo || "").toLowerCase();

    return (
      type.startsWith("image/") ||
      /\.(avif|bmp|gif|jpe?g|png|webp)$/.test(name)
    );
  };

  const viewAttachment = async attachment => {
    const requestId = ++previewRequest.current;
    setPreviewAttachment(attachment);
    setPreviewLoading(true);

    try {
      const response = await api.get(
        `/os/ordens-servico/${ordemServicoId}/anexos/${attachment.id}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(response.data);

      if (requestId !== previewRequest.current) {
        window.URL.revokeObjectURL(url);
        return;
      }

      setPreviewUrl(url);
    } catch (err) {
      if (requestId === previewRequest.current) {
        setPreviewAttachment(null);
        toastError(err);
      }
    } finally {
      if (requestId === previewRequest.current) {
        setPreviewLoading(false);
      }
    }
  };

  const closeAttachmentPreview = () => {
    previewRequest.current += 1;
    setPreviewAttachment(null);
    setPreviewUrl("");
    setPreviewLoading(false);
  };

  useEffect(
    () => () => {
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const selectField = (name, label, options) => (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select name={name} value={form[name]} onChange={change} label={label}>
        {options.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.nome}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const defaultObjects = useMemo(
    () =>
      form.objeto_id
        ? catalogs.objetosPadrao.filter(
            item => String(item.objeto_id) === String(form.objeto_id)
          )
        : catalogs.objetosPadrao,
    [catalogs.objetosPadrao, form.objeto_id]
  );

  const groupStatus = useMemo(
    () =>
      catalogs.status.filter(
        item => String(item.grupo_id) === String(form.grupo_id)
      ),
    [catalogs.status, form.grupo_id]
  );

  const groupTypes = useMemo(
    () =>
      catalogs.tipos.filter(
        item => String(item.grupo_id) === String(form.grupo_id)
      ),
    [catalogs.tipos, form.grupo_id]
  );

  const renderTable = (headers, rows, emptyMessage) => (
    <div className={classes.tableWrapper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={headers.length} className={classes.empty}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  if (loading && !order) {
    return (
      <MainContainer maxWidth={false}>
        <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Tooltip title="Voltar para a listagem">
          <IconButton onClick={() => history.push("/ordens-servico")}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Title>OS #{order?.id}</Title>
        {order?.status && (
          <Chip
            className={classes.headerStatus}
            size="small"
            color={order.data_fechada ? "default" : "primary"}
            label={order.status.nome}
          />
        )}
        <MainHeaderButtonsWrapper>
          {String(order?.grupo_id) === "1" && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AccountTreeIcon />}
              onClick={() => setCreateN2Open(true)}
              disabled={loading || saving}
            >
              Criar N2
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCommentIcon />}
            onClick={() => setInteractionOpen(true)}
            disabled={loading || saving}
          >
            Interagir
          </Button>
          <Button
            startIcon={
              pdfLoading ? (
                <CircularProgress size={18} />
              ) : (
                <PictureAsPdfIcon />
              )
            }
            onClick={openPdf}
            disabled={loading || saving || pdfLoading}
          >
            Imprimir
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadOrder}
            disabled={loading || saving}
          >
            Reconsultar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={save}
            disabled={loading || saving}
          >
            Gravar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <OsInteractionDialog
        open={interactionOpen}
        onClose={() => setInteractionOpen(false)}
        onSaved={interactionSaved}
        order={order}
        objectOptions={catalogs.objetosConclusao}
        canFinalize={!order?.data_fechada}
      />
      <OsRelationshipDialog
        open={relationshipOpen}
        onClose={() => setRelationshipOpen(false)}
        onSaved={relationshipSaved}
        order={order}
      />
      <Dialog
        open={createN2Open}
        onClose={saving ? undefined : () => setCreateN2Open(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Criar N2 filha da OS #{order?.id}</DialogTitle>
        <DialogContent dividers>
          <OsCreateForm
            parentOrder={order}
            onCancel={() => setCreateN2Open(false)}
            onCreated={created => {
              setCreateN2Open(false);
              history.push(`/ordens-servico/${created.id}`);
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(previewAttachment)}
        onClose={closeAttachmentPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {previewAttachment?.nome_arquivo || "Visualizar imagem"}
        </DialogTitle>
        <DialogContent dividers className={classes.imagePreviewContent}>
          {previewLoading ? (
            <CircularProgress />
          ) : (
            previewUrl && (
              <img
                src={previewUrl}
                alt={previewAttachment?.nome_arquivo || "Anexo da OS"}
                className={classes.imagePreview}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAttachmentPreview}>Fechar</Button>
          <Button
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={() => downloadAttachment(previewAttachment)}
            disabled={!previewAttachment || previewLoading}
          >
            Baixar
          </Button>
        </DialogActions>
      </Dialog>

      <Paper className={classes.pagePaper} variant="outlined">
        <div className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={9}>
              <Typography className={classes.sectionTitle}>
                Identificação e classificação
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  {selectField("grupo_id", "Grupo", catalogs.grupos)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {selectField("status_id", "Status", groupStatus)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {selectField(
                    "prioridade_id",
                    "Prioridade",
                    catalogs.prioridades
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={catalogs.clientes}
                    value={
                      catalogs.clientes.find(
                        item => String(item.id) === String(form.cliente_id)
                      ) || null
                    }
                    getOptionLabel={option =>
                      option.razao || option.fantasia || `Cliente ${option.id}`
                    }
                    getOptionSelected={(option, value) => option.id === value.id}
                    onInputChange={(_, value) => setClientSearch(value)}
                    onChange={(_, value) => {
                      const clienteId = value?.id || "";
                      setForm(previous => ({
                        ...previous,
                        cliente_id: clienteId,
                        contato_id: ""
                      }));
                      loadContacts(clienteId);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Cliente"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Contato</InputLabel>
                    <Select
                      name="contato_id"
                      value={form.contato_id}
                      onChange={change}
                      label="Contato"
                    >
                      <MenuItem value="">Sem contato</MenuItem>
                      {catalogs.contatos.map(contact => (
                        <MenuItem key={contact.id} value={contact.id}>
                          {contact.contato || contact.nome}{" "}
                          {contact.numero || contact.telefone || contact.fone
                            ? `— ${contact.numero || contact.telefone || contact.fone}`
                            : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {selectField("objeto_id", "Objeto", catalogs.objetos)}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {selectField(
                    "objeto_padrao_id",
                    "Objeto padrão",
                    defaultObjects
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {selectField("tecnico_id", "Técnico", catalogs.tecnicos)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {selectField("tipo_id", "Tipo", groupTypes)}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {selectField("canal_id", "Canal", catalogs.canais)}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="descricao_curta"
                    label="Descrição curta"
                    value={form.descricao_curta}
                    onChange={change}
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 254 }}
                  />
                </Grid>
              </Grid>

              <Box mt={3}>
                <Typography className={classes.sectionTitle}>
                  Previsão e cobrança
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="data_previsao_inicio"
                      label="Previsão de início"
                      value={form.data_previsao_inicio}
                      onChange={change}
                      type="datetime-local"
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="data_previsao_termino"
                      label="Previsão de término"
                      value={form.data_previsao_termino}
                      onChange={change}
                      type="datetime-local"
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="tempo_estimado"
                      label="Tempo estimado (min)"
                      value={form.tempo_estimado}
                      onChange={change}
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="cobrar"
                          checked={form.cobrar}
                          onChange={change}
                          color="primary"
                        />
                      }
                      label="Cobrar esta OS"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="valor_total"
                      label="Valor total (R$)"
                      value={form.valor_total}
                      onChange={change}
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={!form.cobrar}
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} lg={3}>
              <Paper className={classes.summaryCard} variant="outlined">
                <Typography className={classes.sectionTitle}>
                  Resumo da OS
                </Typography>
                {[
                  ["Aberta em", formatDate(order?.data_hora)],
                  ["Última alteração", formatDate(order?.data_alterada)],
                  ["Fechada em", formatDate(order?.data_fechada)],
                  ["Técnico da inclusão", order?.tecnico_inclusao?.nome || "-"],
                  ["Nível", order?.nivel_id === 1 ? "Padrão" : order?.nivel_id],
                  ["Tempo gasto", formatMinutes(order?.tempo_gasto)],
                  [
                    "Primeira resposta",
                    formatMinutes(order?.tempo_primeira_resposta)
                  ]
                ].map(([label, value]) => (
                  <div className={classes.summaryItem} key={label}>
                    <Typography variant="caption" color="textSecondary">
                      {label}
                    </Typography>
                    <Typography>{value || "-"}</Typography>
                  </div>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </div>

        <AppBar position="static" color="default" elevation={0}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab label="Descrição" />
            <Tab label={`Atualizações (${order?.interacoes?.length || 0})`} />
            <Tab label={`Log (${order?.logs?.length || 0})`} />
            <Tab label={`Relacionadas (${order?.relacionadas?.length || 0})`} />
            <Tab label={`Tempos (${order?.tempos?.length || 0})`} />
            <Tab label={`Objetos (${order?.objetos_vinculados?.length || 0})`} />
            <Tab label={`Anexos (${order?.anexos?.length || 0})`} />
            <Tab label={chatsLoaded ? `Chats (${chats.length})` : "Chats"} />
          </Tabs>
        </AppBar>

        <div className={classes.tabContent}>
          {tab === 0 && (
            <TextField
              className={classes.descriptionField}
              name="descricao"
              label="Descrição completa"
              value={form.descricao}
              onChange={change}
              variant="outlined"
              multiline
              rows={10}
              fullWidth
            />
          )}

          {tab === 1 && (
            <>
              {(order?.interacoes || []).map(interaction => (
                <Paper
                  key={interaction.id}
                  variant="outlined"
                  className={classes.timelineItem}
                >
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle2">
                      {interaction.tecnico?.nome || "Sem técnico"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(interaction.data_hora)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Typography className={classes.preWrap}>
                    {interaction.descricao}
                  </Typography>
                </Paper>
              ))}
              {!order?.interacoes?.length && (
                <Typography className={classes.empty}>
                  Nenhuma atualização registrada.
                </Typography>
              )}
            </>
          )}

          {tab === 2 &&
            renderTable(
              ["ID", "Técnico", "Data/hora", "Descrição"],
              (order?.logs || []).map(log => (
                <TableRow key={log.id}>
                  <TableCell>{log.id}</TableCell>
                  <TableCell>{log.tecnico?.nome || "-"}</TableCell>
                  <TableCell>{formatDate(log.data_hora)}</TableCell>
                  <TableCell className={classes.preWrap}>
                    {log.descricao}
                  </TableCell>
                </TableRow>
              )),
              "Nenhum registro de log."
            )}

          {tab === 3 && (
            <>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AccountTreeIcon />}
                  onClick={() => setRelationshipOpen(true)}
                >
                  Relacionar OS
                </Button>
              </Box>
              {renderTable(
                [
                  "OS",
                  "Relação",
                  "Cliente",
                  "Descrição",
                  "Status",
                  "Última alteração"
                ],
                (order?.relacionadas || []).map(related => (
                  <TableRow
                    hover
                    key={related.id}
                    onClick={() =>
                      history.push(`/ordens-servico/${related.id}`)
                    }
                    style={{ cursor: "pointer" }}
                    title={`Abrir OS #${related.id}`}
                  >
                    <TableCell>{related.id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={related.relacao === "pai" ? "primary" : "default"}
                        variant={
                          related.relacao === "pai" ? "default" : "outlined"
                        }
                        label={related.relacao === "pai" ? "Pai" : "Filha"}
                      />
                    </TableCell>
                    <TableCell>
                      {related.cliente?.razao ||
                        related.cliente?.fantasia ||
                        "-"}
                    </TableCell>
                    <TableCell>{related.descricao_curta}</TableCell>
                    <TableCell>{related.status?.nome || "-"}</TableCell>
                    <TableCell>{formatDate(related.data_alterada)}</TableCell>
                  </TableRow>
                )),
                "Nenhuma OS relacionada."
              )}
            </>
          )}

          {tab === 4 &&
            renderTable(
              ["Técnico", "Status", "Início", "Fim", "Duração", "Extra"],
              (order?.tempos || []).map(period => (
                <TableRow key={period.id}>
                  <TableCell>{period.tecnico?.nome || "-"}</TableCell>
                  <TableCell>{period.status?.nome || "-"}</TableCell>
                  <TableCell>{formatDate(period.data_hora_inicial)}</TableCell>
                  <TableCell>{formatDate(period.data_hora_final)}</TableCell>
                  <TableCell>{periodDuration(period)}</TableCell>
                  <TableCell>{period.extra ? "Sim" : "Não"}</TableCell>
                </TableRow>
              )),
              "Nenhum período de tempo registrado."
            )}

          {tab === 5 &&
            renderTable(
              ["ID", "Objeto", "Técnico"],
              (order?.objetos_vinculados || []).map(link => (
                <TableRow key={link.id}>
                  <TableCell>{link.id}</TableCell>
                  <TableCell>{link.objeto?.nome || "-"}</TableCell>
                  <TableCell>{link.tecnico?.nome || "-"}</TableCell>
                </TableRow>
              )),
              "Nenhum objeto vinculado."
            )}

          {tab === 6 &&
            renderTable(
              ["Arquivo", "Tipo", "Técnico", "Data/hora", ""],
              (order?.anexos || []).map(attachment => (
                <TableRow key={attachment.id}>
                  <TableCell>{attachment.nome_arquivo}</TableCell>
                  <TableCell>{attachment.tipo || "-"}</TableCell>
                  <TableCell>{attachment.tecnico?.nome || "-"}</TableCell>
                  <TableCell>{formatDate(attachment.data_hora)}</TableCell>
                  <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                    {attachment.download_disponivel &&
                      isImageAttachment(attachment) && (
                        <Tooltip title="Visualizar imagem">
                          <IconButton
                            size="small"
                            onClick={() => viewAttachment(attachment)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    {attachment.download_disponivel && (
                      <Tooltip title="Baixar arquivo">
                        <IconButton
                          size="small"
                          onClick={() => downloadAttachment(attachment)}
                        >
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              )),
              "Nenhum anexo registrado."
            )}

          {tab === 7 && (
            <>
              {chatsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                renderTable(
                  [
                    "Atendimento",
                    "Contato",
                    "Canal",
                    "Fila",
                    "Status",
                    "Atendente",
                    "Última mensagem",
                    "Vinculado por",
                    ""
                  ],
                  chats.map(link => (
                    <TableRow
                      hover
                      key={link.id}
                      onClick={() =>
                        history.push(`/tickets/${link.ticket.uuid}`)
                      }
                      style={{ cursor: "pointer" }}
                      title={`Abrir atendimento #${link.ticket.id}`}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <ChatBubbleOutlineIcon
                            color="primary"
                            fontSize="small"
                            style={{ marginRight: 8 }}
                          />
                          #{link.ticket.id}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {link.ticket.contact?.name || "-"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {link.ticket.contact?.number || ""}
                        </Typography>
                      </TableCell>
                      <TableCell>{link.ticket.channel || "-"}</TableCell>
                      <TableCell>{link.ticket.queue?.name || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={
                            link.ticket.status === "open"
                              ? "primary"
                              : "default"
                          }
                          variant="outlined"
                          label={
                            {
                              open: "Aberto",
                              pending: "Aguardando",
                              closed: "Fechado"
                            }[link.ticket.status] || link.ticket.status
                          }
                        />
                      </TableCell>
                      <TableCell>{link.ticket.user?.name || "-"}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{
                            maxWidth: 280,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {link.ticket.lastMessage || "-"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(link.ticket.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {link.createdByUser?.name || "Automação"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Abrir atendimento">
                          <IconButton size="small">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )),
                  "Nenhum chat vinculado a esta OS."
                )
              )}
            </>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default OrdensServicoDetalhe;
